import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography,
  useTheme
} from "@mui/material";

import {
  ArrowsClockwiseIcon,
  HourglassHighIcon,
  VideoCameraIcon,
  WarningIcon,
  XCircleIcon
} from "@phosphor-icons/react";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { useNavigate } from "react-router-dom";

import { TUTRED_CLIENT_HOSTNAME } from "@/constants";
import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { useLessonTimer } from "@/hooks/useLessonTimer";
import { colors } from "@/theme";
import { Recording, UserRole } from "@/types";
import { storage } from "@/utils/firebase";

import LessonDetails from "../LessonDetails";
import Sidebar, { SidebarState } from "../Sidebar";

const RecordedLesson = () => {
  const theme = useTheme();
  const { lesson } = useLesson();
  const [recordingInfo, setRecordingInfo] = useState<Recording | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarState, setSidebarState] = useState(SidebarState.STANDARD);

  const { userRole, user, aiAxios } = useFirebaseContext();
  const navigate = useNavigate();
  const db = getFirestore();

  const userName =
    userRole === UserRole.Tutor ? lesson?.studentName : lesson?.tutorName;
  const userPhoto =
    userRole === UserRole.Tutor
      ? lesson?.studentPhotoUrl
      : lesson?.tutorPhotoUrl;
  const otherUserId =
    userRole === UserRole.Tutor ? lesson?.studentId : lesson?.tutorId;

  const { remainingMinutes, progressPercent } = useLessonTimer(lesson ?? null);

  useEffect(() => {
    if (!lesson) return;
    if (!lesson.recordingEnabled || !lesson.hasCompleted) {
      setIsLoading(false);
      return;
    }
    const recordingRef = doc(db, "recordings", lesson.id);

    const unsubscribe = onSnapshot(recordingRef, (doc) => {
      const recording = doc.data() as Recording;
      setRecordingInfo({
        ...recording,
        id: doc.id
      } as Recording);
      if (!recording || recording.status !== "completed") {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [db, lesson]);

  useEffect(() => {
    if (!lesson?.id || !recordingInfo || recordingInfo.status !== "completed") return;

    const fetchDownloadUrl = async () => {
      try {
        const pathOrUrl = recordingInfo.recordingUrl;
        if (!pathOrUrl) return;

        if (pathOrUrl.startsWith("http")) {
          // Fallback for legacy URLs already containing tokens
          setDownloadUrl(pathOrUrl);
        } else {
          // New secure path format: fetch a fresh, rule-checked URL
          const url = await getDownloadURL(ref(storage, pathOrUrl));
          setDownloadUrl(url);
        }
      } catch (error) {
        console.error("Error fetching fresh download URL:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDownloadUrl();
  }, [lesson?.id, recordingInfo?.status]);

  const handleRegenerateRecording = async () => {
    if (!lesson?.id || isRegenerating) return;

    try {
      setIsRegenerating(true);
      await aiAxios.post(`/lesson-processor/regenerate-recording`, {
        lesson_id: lesson.id,
      });
    } catch (error) {
      console.error("Error regenerating recording:", error);
    } finally {
      setIsRegenerating(false);
    }
  };

  const renderRecordingContent = () => {
    if (recordingInfo?.status === "failed") {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            height: "100%",
            w: "100%",
            p: 4,
            textAlign: "center"
          }}
        >
          <WarningIcon size={64} weight="fill" color={colors.red.main} />
          <Typography variant="text20B" color="textPrimary" sx={{
            mt: 2,
            mb: 1
          }}>
            Recording Failed
          </Typography>
          <Typography variant="text16R" color="textSecondary" sx={{
            mb: 3,
            maxWidth: 400
          }}>
            The recording for this lesson failed to process. You can try
            regenerating it.
          </Typography>
          <Button
            variant="contained"
            onClick={handleRegenerateRecording}
            disabled={isRegenerating}
            startIcon={
              <Box
                component="span"
                sx={
                  isRegenerating
                    ? {
                      display: "inline-flex",
                      animation: "spin 1s linear infinite"
                    }
                    : {
                      display: "inline-flex"
                    }
                }
              >
                <ArrowsClockwiseIcon size={20} />
              </Box>
            }
            sx={{
              bgcolor: colors.primary,
              "&:hover": { bgcolor: colors.primary },
              minWidth: 160,
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" }
              }
            }}
          >
            {isRegenerating ? "Regenerating..." : "Try Again"}
          </Button>
        </Stack>
      );
    }

    if (recordingInfo?.status === "lesson-in-progress") {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            height: "100%",
            w: "100%",
            p: 4,
            textAlign: "center"
          }}
        >
          <HourglassHighIcon size={64} weight="fill" color={colors.yellow} />
          <Typography variant="text20B" color="textPrimary" sx={{
            mt: 2,
            mb: 1
          }}>
            Lesson Still in Progress
          </Typography>
          <Typography variant="text16R" color="textSecondary" sx={{
            my: 1
          }}>
            The lesson has not yet ended. You can generate the recording
            now, but it may be incomplete.
          </Typography>
          <Button
            variant="contained"
            onClick={handleRegenerateRecording}
            disabled={isRegenerating}
            startIcon={
              <Box
                component="span"
                sx={
                  isRegenerating
                    ? {
                      display: "inline-flex",
                      animation: "spin 1s linear infinite"
                    }
                    : {
                      display: "inline-flex"
                    }
                }
              >
                <ArrowsClockwiseIcon size={20} />
              </Box>
            }
            sx={{
              bgcolor: colors.primary,
              "&:hover": { bgcolor: colors.primary },
              minWidth: 220,
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" }
              }
            }}
          >
            {isRegenerating ? "Generating..." : "Generate Anyway"}
          </Button>
        </Stack>
      );
    }

    if (downloadUrl) {
      return (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Box
            component="video"
            controls
            preload="metadata"
            poster="/images/video-poster.png"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              maxWidth: "100%",
              maxHeight: "100%"
            }}
          >
            <source src={downloadUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </Box>

        </Box>
      );
    }

    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          height: "100%",
          textAlign: "center",
          color: "textPrimary"
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.5 }
            }
          }}
        >
          <VideoCameraIcon size={64} weight="duotone" />
        </Box>
        <Typography variant="text20B" color="textPrimary">
          Loading Recording...
        </Typography>
        <Typography variant="text16R" color="textSecondary" sx={{ my: 1 }}>
          Preparing video playback, this may take a few minutes.
        </Typography>
        <CircularProgress
          size={32}
          thickness={4}
          sx={{ color: theme.palette.secondary.main }}
        />
      </Stack>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme.palette.background.default
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!lesson || lesson.studentId !== user?.uid) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Card
          variant="outlined"
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            textAlign: "center",
            maxWidth: 440,
            width: "100%"
          }}
        >
          <XCircleIcon size={80} weight="fill" color={colors.red.main} />
          <Box>
            <Typography variant="text20B" color="textPrimary" sx={{ mb: 1 }}>
              Lesson Not Found
            </Typography>
            <Typography variant="text16R" color="textSecondary" sx={{ my: 1 }}>
              The lesson you&apos;re looking for doesn&apos;t exist or you don&apos;t have
              permission to view it.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => (window.location.href = TUTRED_CLIENT_HOSTNAME)}
          >
            Back to Home
          </Button>
        </Card>
      </Box>
    );
  }

  // Check if recording was enabled
  if (!lesson.recordingEnabled) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Card
          variant="outlined"
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            textAlign: "center",
            maxWidth: 440,
            width: "100%"
          }}
        >
          <VideoCameraIcon size={80} weight="fill" color={colors.secondary} />
          <Box>
            <Typography variant="text20B" color="textPrimary" sx={{ mb: 1 }}>
              Recording Not Available
            </Typography>
            <Typography variant="text16R" color="textSecondary" sx={{ my: 1 }}>
              Recording was not enabled for this lesson. No recording is available
              to view.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() =>
              (window.location.href = `${TUTRED_CLIENT_HOSTNAME}student/lessons/past/${lesson.id}`)
            }
          >
            View Lesson Summary
          </Button>
        </Card>
      </Box>
    );
  }

  // Check if lesson is completed
  if (!lesson.hasCompleted) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Card
          variant="outlined"
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            textAlign: "center",
            maxWidth: 440,
            width: "100%"
          }}
        >
          <HourglassHighIcon size={80} weight="fill" color={colors.yellow} />
          <Box>
            <Typography variant="text20B" color="textPrimary" sx={{ mb: 1 }}>
              Lesson Still in Progress
            </Typography>
            <Typography variant="text16R" color="textSecondary" sx={{ my: 1 }}>
              This lesson has not been completed yet. You can only view recordings
              of completed lessons.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/${lesson.id}`)}
          >
            Join Lesson
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        bgcolor: "background.default"
      }}
    >
      <Stack sx={{ flexShrink: 0 }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          px={3.5}
          py={1}
          borderBottom={1}
          borderColor="divider"
          bgcolor="background.default"
        >
          {lesson && (
            <LessonDetails
              lessonInfo={lesson}
              participantName={userName}
              participantPhoto={userPhoto}
              participantUserId={otherUserId}
              remainingMinutes={remainingMinutes}
              progressPercent={progressPercent}
              showSessionStatus={false}
            />
          )}
        </Stack>
      </Stack>
      <Stack
        flex={1}
        py={1}
        px={2}
        direction="row"
        spacing={2}
        sx={{
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            flex: 1,
            bgcolor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          {renderRecordingContent()}
        </Box>
        <Sidebar
          userRole={userRole}
          sidebarState={sidebarState}
          setSidebarState={setSidebarState}
          inRecordedLessonView={true}
          lessonId={lesson.id}
          lessonType={lesson.type}
        />
      </Stack>
    </Stack>
  );
};

export default RecordedLesson;
